import React, { useState } from "react";
import { Link } from "react-router-dom";
import HeroIcon from "../common/HeroIcon";
import {
  formatNumber,
  formatRatioCompact,
  getBrandFitLabel,
  getCreatorImpactLabel,
  getOpportunityLabelTint,
  getReachLabel,
  LABEL_TINT_CLASSES,
} from "../patterns/utils";

const OPPORTUNITY_EMOJI = {
  "High Opportunity": "🔥",
  "Possible Gem": "💎",
  Rising: "📈",
  Established: "✓",
  Saturated: "⚠",
  Declining: "📉",
};

const PATTERN_TYPE_ICONS = {
  type: "document-text",
  main_topic: "tag",
  content_angle: "light-bulb",
  format: "squares-2x2",
  structure: "queue-list",
  categories: "folder",
  tone: "swatch",
  audience: "users",
  language: "language",
  hook_type: "bolt",
  hook_phrase: "chat-bubble-left-ellipsis",
  hook_visual: "eye",
  cta_type: "cursor-arrow-rays",
  cta_style: "megaphone",
  hashtags: "hashtag",
  channel_tags: "at-symbol",
  text_hook: "bars-3-bottom-left",
  song: "musical-note",
  artist: "user-circle",
};

const VERDICT_CARD_CLASS =
  "rounded-lg border border-theme-shadow bg-theme-main p-3 flex flex-col h-full";

const StatCard = ({
  title,
  result,
  resultClassName,
  metric,
  metricClassName,
  metricSubtitle,
  subtitle,
  subtitleLines = 2,
}) => {
  const subtitleClamp =
    subtitleLines === 3 ? "line-clamp-3" : subtitleLines === 4 ? "line-clamp-4" : "line-clamp-2";

  return (
    <div className={VERDICT_CARD_CLASS}>
      <p className="text-xs font-semibold text-theme-text">{title}</p>
      {result ? (
        <p
          className={
            resultClassName ??
            "text-sm font-bold text-theme-text flex items-center gap-1.5 mt-1 leading-tight"
          }
        >
          {result}
        </p>
      ) : null}
      {metric != null ? (
        <p
          className={
            metricClassName ??
            "text-xl font-bold text-theme-text min-h-[1.75rem] mt-1 leading-tight"
          }
        >
          {metric}
        </p>
      ) : null}
      {metricSubtitle ? (
        <p className="text-xs text-theme-text opacity-80 leading-relaxed mt-0.5">{metricSubtitle}</p>
      ) : null}
      {subtitle ? (
        <p className={`text-xs text-theme-text opacity-80 leading-relaxed mt-1 ${subtitleClamp}`}>
          {subtitle}
        </p>
      ) : null}
    </div>
  );
};

const IdeaQueueCard = ({
  idea,
  onCreateScript,
  onReject,
  onSkip,
  isGeneratingScript = false,
}) => {
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const patterns = Array.isArray(idea?.patterns) ? idea.patterns : [];
  const primaryPattern = patterns[0] || null;
  const analysis = primaryPattern?.llm_analysis;
  const stats = primaryPattern?.stats || {};
  const medianViews = stats.views;
  const creatorRatio = primaryPattern?.creator_comparison?.composite_outlier;
  const reachRatio = primaryPattern?.baseline_comparison?.views;
  const creatorLabel = getCreatorImpactLabel(creatorRatio);
  const reachLabel = getReachLabel(reachRatio);
  const brandFitScore = idea?.brand_fit_score;
  const brandFitReasoning = idea?.brand_fit_reasoning;
  const brandFitLabel = getBrandFitLabel(brandFitScore);
  const hasBrandFit = brandFitScore != null;
  const opportunityTint = getOpportunityLabelTint(analysis?.opportunity_label);
  const patternIcon = PATTERN_TYPE_ICONS[primaryPattern?.pattern_type] || "squares-2x2";
  const hasAnalysis = Boolean(
    analysis?.description ||
      analysis?.how_it_performed ||
      (Array.isArray(analysis?.why) && analysis.why.length > 0) ||
      analysis?.opportunity_label,
  );
  const ideaUrl = idea?.hash ? `/ideas/${encodeURIComponent(idea.hash)}/overview/` : null;

  return (
    <div className="relative rounded-2xl border border-theme-shadow bg-theme-secondary p-5 sm:p-6 shadow-xl space-y-4 min-w-0 max-w-full overflow-hidden">
      <div className="flex items-center gap-2.5 min-w-0">
        {idea?.emoji ? <span className="text-3xl leading-none flex-shrink-0">{idea.emoji}</span> : null}
        {ideaUrl ? (
          <Link
            to={ideaUrl}
            className="text-xl sm:text-2xl font-semibold text-theme-text leading-tight min-w-0 hover:underline"
          >
            {idea.title || "Untitled idea"}
          </Link>
        ) : (
          <h3 className="text-xl sm:text-2xl font-semibold text-theme-text leading-tight min-w-0">
            {idea?.title || "Untitled idea"}
          </h3>
        )}
      </div>

      <section className="space-y-3">
        <p className="text-sm sm:text-base text-theme-text opacity-90 leading-relaxed line-clamp-2">
          {idea?.content || "No idea details provided."}
        </p>
        {idea?.why_this_works ? (
          <div className="flex items-start gap-2 border-l-2 border-theme-primary pl-3">
            <HeroIcon name="light-bulb" className="w-4 h-4 text-theme-primary flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-theme-primary mb-0.5">
                Why this idea fits
              </p>
              <p className="text-sm text-theme-text opacity-75 leading-relaxed line-clamp-2">
                {idea.why_this_works}
              </p>
            </div>
          </div>
        ) : null}
      </section>

      {primaryPattern ? (
        <section className="space-y-3 border-t border-theme-shadow pt-4">
          <div className="flex items-center justify-between gap-3 min-w-0">
            <h4 className="text-sm font-semibold text-theme-text flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
              <HeroIcon name={patternIcon} className="w-4 h-4 text-theme-text opacity-70 flex-shrink-0" />
              <span
                className="truncate"
                title={primaryPattern.display_label || primaryPattern.name || "Pattern"}
              >
                {primaryPattern.display_label || primaryPattern.name || "Pattern"}
              </span>
            </h4>
            {hasAnalysis ? (
              <button
                type="button"
                onClick={() => setAnalysisOpen(true)}
                className="text-xs font-medium text-theme-primary hover:opacity-80 flex-shrink-0"
              >
                View analysis
              </button>
            ) : null}
          </div>
          <div
            className={`grid gap-3 items-stretch ${
              hasBrandFit ? "grid-cols-2 lg:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"
            }`}
          >
            <StatCard
              title="Reach"
              result={
                reachLabel ? (
                  <>
                    <span>{reachLabel.emoji}</span>
                    {reachLabel.label}
                  </>
                ) : null
              }
              resultClassName={`text-sm font-bold flex items-center gap-1.5 mt-1 leading-tight ${
                reachLabel ? LABEL_TINT_CLASSES[reachLabel.tint] ?? "text-theme-text" : "text-theme-text"
              }`}
              metric={medianViews != null ? formatNumber(medianViews) : "—"}
              metricSubtitle={`median views (${formatRatioCompact(reachRatio)?.text ?? "—"} audience avg)`}
            />
            <StatCard
              title="Creator impact"
              result={
                creatorLabel ? (
                  <>
                    <span>{creatorLabel.emoji}</span>
                    {creatorLabel.label}
                  </>
                ) : null
              }
              resultClassName={`text-sm font-bold flex items-center gap-1.5 mt-1 leading-tight ${
                creatorLabel ? LABEL_TINT_CLASSES[creatorLabel.tint] ?? "text-theme-text" : "text-theme-text"
              }`}
              metric={formatRatioCompact(creatorRatio)?.text ?? "—"}
              metricSubtitle="vs typical creator video"
            />
            <StatCard
              title="Opportunity"
              metric={
                analysis?.opportunity_label ? (
                  <>
                    <span>{OPPORTUNITY_EMOJI[analysis.opportunity_label] ?? "✓"}</span>
                    {analysis.opportunity_label}
                  </>
                ) : (
                  "—"
                )
              }
              metricClassName={`text-base font-bold flex items-center gap-1.5 min-h-[1.75rem] mt-1 leading-tight ${
                analysis?.opportunity_label
                  ? LABEL_TINT_CLASSES[opportunityTint] ?? "text-theme-text"
                  : "text-theme-text"
              }`}
              subtitleLines={3}
              subtitle={
                analysis?.opportunity_description ||
                (stats.video_count != null ? `${stats.video_count} videos in pattern` : null)
              }
            />
            {hasBrandFit ? (
              <StatCard
                title="Brand fit"
                metric={
                  <>
                    {brandFitScore}
                    <span className="text-sm font-medium text-theme-text opacity-80"> / 100</span>
                  </>
                }
                subtitleLines={3}
                subtitle={
                  brandFitLabel
                    ? `${brandFitLabel.emoji} ${brandFitLabel.label}${brandFitReasoning ? ` · ${brandFitReasoning}` : ""}`
                    : brandFitReasoning || "—"
                }
              />
            ) : null}
          </div>
        </section>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {ideaUrl ? (
          <Link
            to={ideaUrl}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-theme-shadow px-3 py-3 text-sm font-medium text-theme-text hover:bg-theme-main transition-colors"
          >
            <HeroIcon name="eye" className="w-4 h-4" />
            View
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => onReject?.(idea)}
          disabled={isGeneratingScript}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-theme-shadow px-3 py-3 text-sm font-medium text-theme-text hover:bg-theme-main disabled:opacity-50 transition-colors"
        >
          <HeroIcon name="x-mark" className="w-4 h-4" />
          Reject
        </button>
        <button
          type="button"
          onClick={() => onSkip?.(idea)}
          disabled={isGeneratingScript}
          className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-theme-shadow px-3 py-3 text-sm font-medium text-theme-text hover:bg-theme-main disabled:opacity-50 transition-colors"
        >
          <HeroIcon name="arrow-right" className="w-4 h-4" />
          Skip
        </button>
        <button
          type="button"
          onClick={() => onCreateScript?.(idea)}
          disabled={isGeneratingScript}
          className="inline-flex flex-1 min-w-[150px] items-center justify-center gap-2 rounded-xl bg-theme-primary px-4 py-3 text-sm font-semibold text-theme-button hover:opacity-90 disabled:opacity-50 transition-opacity sm:flex-none sm:ml-auto"
        >
          <HeroIcon name="document-text" className="w-4 h-4" />
          {isGeneratingScript ? "Creating..." : "Create Script"}
        </button>
      </div>

      {analysisOpen && analysis ? (
        <>
          <div className="fixed inset-0 z-30" aria-hidden onClick={() => setAnalysisOpen(false)} />
          <div className="absolute right-4 top-12 z-40 w-80 max-w-[calc(100%-2rem)] rounded-xl border border-theme-shadow bg-theme-main p-4 shadow-xl">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-sm font-semibold text-theme-text">Pattern analysis</p>
              <button
                type="button"
                onClick={() => setAnalysisOpen(false)}
                className="text-theme-text opacity-60 hover:opacity-100"
                aria-label="Close pattern analysis"
              >
                <HeroIcon name="x-mark" className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4 max-h-80 overflow-y-auto">
              {analysis.description ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-theme-text opacity-50 mb-1">
                    Definition
                  </p>
                  <p className="text-xs text-theme-text leading-relaxed">{analysis.description}</p>
                </div>
              ) : null}
              {Array.isArray(analysis.why) && analysis.why.length > 0 ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-theme-text opacity-50 mb-1">
                    Why it works
                  </p>
                  <ul className="space-y-1">
                    {analysis.why.slice(0, 4).map((item, index) => (
                      <li key={`${item.reason}-${index}`} className="text-xs text-theme-text flex gap-1.5">
                        <span>{item.emoji}</span>
                        <span>{item.reason}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {Array.isArray(analysis.best_use_cases) && analysis.best_use_cases.length > 0 ? (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-theme-text opacity-50 mb-1">
                    Best use cases
                  </p>
                  <ul className="space-y-1 list-disc list-inside">
                    {analysis.best_use_cases.slice(0, 4).map((item) => (
                      <li key={item} className="text-xs text-theme-text">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default IdeaQueueCard;
